import React, { useState } from 'react';
import { Button, Dialog, DialogTrigger, DialogContent, Heading, Select, Text, type TagOption } from '@chatsdet/ui';
import { Clock, PencilSimple } from '@phosphor-icons/react';
import { useTagsQuery } from '../../hooks/useTags';
import { browserTimezone, describeCadence, useParseSchedule } from '../scheduling';
import { AlertDestinationEditor } from './AlertDestinationEditor';
import {
  describeScheduleTarget,
  useSuiteSchedules,
  useCreateSuiteSchedule,
  useUpdateScheduleAlerts,
  useUpdateScheduleTiming,
  useScheduleEnvironmentOptions,
  type SuiteScheduleTargetInput,
} from './useSuiteSchedules';
import {
  ScheduleDescriptionComposer,
  SchedulePresetButtons,
  SuiteScheduleActiveList,
  SuiteScheduleTargetPicker,
  type SchedulePreview,
  type SuiteScheduleTargetKey,
} from './ScheduleSuiteModalParts';
import { useTestSuite, useTestSuites, useUpdateSuiteAlerts } from './useTestSuites';
import type { SuiteAlertConfig, TestSuiteSchedule } from './types';

/**
 * Schedule recurring test runs via the automations framework. Two entry points
 * share this modal:
 *
 * - **Suite-locked** (`suiteId` set) — the Suite Builder clock icon. The target
 *   is set & locked to that suite (no picker); the active-schedule list is
 *   scoped to it.
 * - **Picker** (no `suiteId`) — the "New schedule" button in Suites config.
 *   A dropdown chooses all eligible workflows or workflows by tag.
 *
 * Pass `schedule` to edit an existing schedule instead: the same cadence
 * presets / description composer and the same alerts editor, prefilled, each
 * saving to that schedule in place. Its target is fixed, so the picker, suite
 * alerts and active-schedule list are not shown.
 *
 * Pass a custom `trigger` to control the opener; defaults to the clock icon.
 */
export function ScheduleSuiteModal({
  suiteId,
  suiteName,
  trigger,
  externalAreas,
  schedule: editing,
}: {
  suiteId?: string;
  suiteName?: string;
  trigger?: React.ReactElement;
  /**
   * Scope the schedule to these external-suite area paths instead of the
   * whole suite (empty/omitted means every area). Requires `suiteId`.
   */
  externalAreas?: string[];
  /** Edit this existing schedule rather than creating a new one. */
  schedule?: TestSuiteSchedule;
}) {
  const locked = Boolean(suiteId);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<SchedulePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<SuiteScheduleTargetKey>('all');
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState<string | null>(null);
  const [scheduleAlertConfig, setScheduleAlertConfig] = useState<SuiteAlertConfig>({ steps: [] });
  const [environmentId, setEnvironmentId] = useState<string | null>(null);

  // Suite-locked modal scopes its list to that suite; the picker lists them all.
  const schedulesQuery = useSuiteSchedules(locked ? suiteId : undefined, open && !editing);
  const tagsQuery = useTagsQuery();
  const suitesQuery = useTestSuites(open && !locked && !editing);
  const suiteQuery = useTestSuite(open && locked ? suiteId : undefined);
  const updateSuiteAlerts = useUpdateSuiteAlerts();
  const parseMutation = useParseSchedule();
  const createMutation = useCreateSuiteSchedule();
  const updateTiming = useUpdateScheduleTiming();
  const updateScheduleAlerts = useUpdateScheduleAlerts();
  const savePending = editing ? updateTiming.isPending : createMutation.isPending;

  // Only a named native suite runs in a chosen environment; tag/all schedules
  // and external suites ignore it, so the picker is hidden for them.
  const scheduledSuite = locked
    ? suiteQuery.data
    : target === 'suite'
      ? suitesQuery.data?.find((suite) => suite.id === selectedSuiteId)
      : undefined;
  const showEnvironment = scheduledSuite?.kind === 'native';
  const environmentOptions = useScheduleEnvironmentOptions(open && showEnvironment);

  const schedules = schedulesQuery.data?.schedules ?? [];
  // Narrowed once, so the suite-alerts editor's `onSave` doesn't need a
  // non-null assertion on `suiteQuery.data` inside its closure.
  const lockedSuite = locked ? suiteQuery.data : undefined;

  const targetInput = (): SuiteScheduleTargetInput | null => {
    if (locked && suiteId) {
      return { target: 'suite', suiteId };
    }
    if (target === 'tag') {
      if (selectedTags.length === 0) return null;
      return { target: 'tag', tags: selectedTags.map((t) => t.name) };
    }
    if (target === 'suite') {
      return selectedSuiteId ? { target: 'suite', suiteId: selectedSuiteId } : null;
    }
    return { target: 'all' };
  };

  const save = (cron: string, description: string) => {
    if (editing) {
      setError(null);
      updateTiming.mutate(
        { ruleId: editing.ruleId, cron, timezone: editing.timezone, description },
        {
          onSuccess: () => {
            setText('');
            setPreview(null);
          },
          onError: (e) => setError(e instanceof Error ? e.message : 'Failed to update schedule'),
        }
      );
      return;
    }
    const resolved = targetInput();
    if (!resolved) {
      setError(
        target === 'tag'
          ? 'Pick at least one tag for a by-tag schedule.'
          : 'Choose a suite to schedule.'
      );
      return;
    }
    setError(null);
    createMutation.mutate(
      {
        cron,
        timezone: browserTimezone(),
        description,
        target: resolved,
        alertConfig: scheduleAlertConfig,
        externalAreas,
        environmentId: showEnvironment ? environmentId : null,
      },
      {
        onSuccess: () => {
          setText('');
          setPreview(null);
          // Don't carry this schedule's alerts over to the next one.
          setScheduleAlertConfig({ steps: [] });
          setEnvironmentId(null);
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Failed to create schedule'),
      }
    );
  };

  const handleParse = () => {
    if (!text.trim()) return;
    setError(null);
    parseMutation.mutate(text.trim(), {
      onSuccess: (data) => setPreview({ cron: data.cron, description: data.description }),
      onError: (e) =>
        setError(e instanceof Error ? e.message : 'Could not interpret that schedule'),
    });
  };

  const tagMissing =
    !locked &&
    ((target === 'tag' && selectedTags.length === 0) ||
      (target === 'suite' && selectedSuiteId === null));

  return (
    <DialogTrigger
      isOpen={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Don't show a previous attempt's error or unapplied preview on reopen.
        setError(null);
        setPreview(null);
        setText('');
      }}
      size="4xl"
    >
      {trigger ?? (
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Schedule runs"
          iconLeft={<Clock size={16} />}
        />
      )}
      <Dialog
        title={
          editing
            ? 'Edit schedule'
            : externalAreas && externalAreas.length > 0
            ? `Schedule ${externalAreas.map(areaShortLabel).join(', ')}`
            : 'Schedule runs'
        }
        eyebrow={locked ? 'Test suite' : 'Schedules'}
        description={
          editing
            ? `${describeScheduleTarget(editing)} — change when it runs and who is alerted.`
            : externalAreas && externalAreas.length > 0 && suiteName
            ? `Run this area of ${suiteName} automatically on a recurring schedule.`
            : locked && suiteName
              ? `Run ${suiteName} automatically on a recurring schedule.`
              : 'Run tests automatically on a recurring schedule.'
        }
      >
        <DialogContent>
          <div className="flex min-w-0 flex-col gap-5">
            {/* Suite destinations are edited beside its schedules: the same config
                applies to manual/API runs too, while a schedule can still add its
                own alerts below. Only shown when locked to a suite — the picker
                (no suiteId) has no single suite to attach these to. */}
            {!editing && lockedSuite && (
              <section className="space-y-4 rounded-lg border border-border bg-card p-5">
                <AlertDestinationEditor
                  title="Suite alerts"
                  description="Send on every completion, including manual and scheduled runs."
                  alertConfig={lockedSuite.alertConfig}
                  isPending={updateSuiteAlerts.isPending}
                  onSave={(alertConfig) => updateSuiteAlerts.mutateAsync({ id: lockedSuite.id, alertConfig })}
                  headingLevel={2}
                />
              </section>
            )}

            <section className="space-y-5 rounded-lg border border-border bg-card p-5">
              <div>
                <Heading level={2}>{editing ? 'Timing' : 'New schedule'}</Heading>
                <Text size="xs" className="mt-0.5 text-muted-foreground text-pretty">
                  {editing
                    ? `Current cadence: ${describeCadence(editing.cron, editing.timezone)}. Pick a new cadence to replace it.`
                    : locked
                    ? 'Set up a new recurring run for this suite.'
                    : 'Set up a new recurring run — choose what to run below.'}
                </Text>
              </div>

              {!locked && !editing && (
                <SuiteScheduleTargetPicker
                  target={target}
                  onTargetChange={setTarget}
                  tags={(tagsQuery.data?.data ?? []) as TagOption[]}
                  selectedTags={selectedTags}
                  onSelectedTagsChange={setSelectedTags}
                  tagsLoading={tagsQuery.isLoading}
                  suites={(suitesQuery.data ?? []).map(({ id, name, kind }) => ({ id, name, kind }))}
                  selectedSuiteId={selectedSuiteId}
                  onSelectedSuiteIdChange={setSelectedSuiteId}
                />
              )}

              {showEnvironment && (
                <Select
                  label="Environment"
                  description="Run in this environment instead of the suite's default."
                  items={[{ id: 'none', label: 'Suite default' }, ...environmentOptions]}
                  selectedKey={environmentId ?? 'none'}
                  onSelectionChange={(key) => setEnvironmentId(key === 'none' ? null : String(key))}
                />
              )}

              <SchedulePresetButtons
                tagMissing={tagMissing}
                isPending={savePending}
                helpText={
                  editing
                    ? 'Replaces the schedule’s cadence immediately. Its alerts are unchanged.'
                    : 'Creates the schedule immediately, with the alerts set below.'
                }
                onCreate={save}
              />
              <ScheduleDescriptionComposer
                text={text}
                preview={preview}
                error={error}
                tagMissing={tagMissing}
                parsePending={parseMutation.isPending}
                createPending={savePending}
                submitLabel={editing ? 'Apply' : 'Create'}
                onTextChange={(value) => {
                  setText(value);
                  setPreview(null);
                }}
                onPreview={handleParse}
                onCreate={save}
              />

              <div className="border-t border-border pt-5">
                <AlertDestinationEditor
                  title="Alerts for this schedule"
                  description={
                    editing
                      ? 'Send only for runs started by this schedule. Changes save right away.'
                      : "Send only for runs started by this schedule — included whether it's created from a quick preset or the description above."
                  }
                  alertConfig={editing ? editing.alertConfig : scheduleAlertConfig}
                  isPending={editing ? updateScheduleAlerts.isPending : createMutation.isPending}
                  onSave={(alertConfig) => {
                    if (editing) {
                      return updateScheduleAlerts.mutateAsync({ ruleId: editing.ruleId, alertConfig });
                    }
                    setScheduleAlertConfig(alertConfig);
                    return Promise.resolve();
                  }}
                />
              </div>
            </section>

            {!editing && (
              <section className="space-y-4 rounded-lg border border-border bg-card p-5">
                <SuiteScheduleActiveList
                  schedules={schedules}
                  isLoading={schedulesQuery.isLoading}
                  locked={locked}
                  renderEdit={(row) => <EditScheduleButton schedule={row} />}
                />
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}

/** Last path segment, for a short area name in the schedule dialog's title. */
function areaShortLabel(path: string): string {
  const segments = path.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? path;
}

/** Row-level edit control: opens the schedule form in edit mode for one schedule. */
export function EditScheduleButton({ schedule }: { schedule: TestSuiteSchedule }) {
  return (
    <ScheduleSuiteModal
      schedule={schedule}
      trigger={
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Edit schedule"
          iconLeft={<PencilSimple size={15} />}
        />
      }
    />
  );
}
